<template>
    <div>
        <div v-if="loading" class="columns is-mobile is-centered">
            <LoadingSpinner2 class="has-added-margin" />
        </div>

        <div class="columns" v-else-if="isEmpty">
            <div class="column is-6 is-offset-3">
                <div class="notification is-info is-light has-text-centered">
                    <span class="icon">
                        <i class="fa fa-info-circle" />
                    </span>
                    <span>
                        {{ translate("record_profile.statistics.no_data") }}
                    </span>
                </div>
            </div>
        </div>

        <template v-else-if="statistics">
            <div class="donuts">
                <div class="donut-block" v-for="donut in donuts" :key="donut.title">
                    <p class="donut-title has-text-weight-semibold">
                        {{ donut.title }}
                    </p>
                    <div
                        class="donut"
                        :class="{ 'donut-empty': donut.slices.length === 0 }"
                        :style="donut.slices.length ? { background: gradientFor(donut.slices) } : {}"
                    >
                        <div class="donut-hole">
                            <span class="donut-count is-size-4 has-text-weight-bold">{{ donut.total }}</span>
                            <span class="donut-caption is-size-7 has-text-grey-light">
                                {{ translate("record_profile.statistics.by_finish") }}
                            </span>
                        </div>
                    </div>
                    <ul class="legend" v-if="donut.slices.length">
                        <li v-for="slice in donut.slices" :key="slice.key" class="legend-row">
                            <span class="legend-swatch" :style="{ background: colorForBucket(slice.key) }" />
                            <span class="legend-label has-text-white">{{ bucketLabel(slice.key) }}</span>
                            <span class="legend-value has-text-grey-light"> {{ slice.count }} · {{ slice.percentage }}% </span>
                        </li>
                    </ul>
                    <p class="has-text-grey-light is-size-7" v-else>—</p>
                </div>
            </div>

            <div class="table-container is-hidden-touch mt-5">
                <table class="table is-fullwidth is-hoverable">
                    <thead>
                        <tr>
                            <th>{{ translate("record_profile.method") }}</th>
                            <th class="has-text-right">{{ translate("record_profile.result.win") }}</th>
                            <th class="has-text-right">{{ translate("record_profile.result.loss") }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in statistics.methods" :key="row.key">
                            <td>{{ translate(row.translationKey) }}</td>
                            <td class="has-text-right has-text-success">{{ row.wins || "—" }}</td>
                            <td class="has-text-right has-text-danger">{{ row.losses || "—" }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="is-hidden-desktop method-cards mt-4">
                <div v-for="row in statistics.methods" :key="row.key" class="box has-no-glow-effect method-card">
                    <p class="has-text-white has-text-weight-semibold mb-2">
                        {{ translate(row.translationKey) }}
                    </p>
                    <div class="is-flex is-align-items-center method-counts">
                        <span class="is-size-7">
                            <span class="has-text-grey-light">{{ translate("record_profile.result.win") }}: </span>
                            <span class="has-text-success has-text-weight-bold">{{ row.wins }}</span>
                        </span>
                        <span class="is-size-7">
                            <span class="has-text-grey-light">{{ translate("record_profile.result.loss") }}: </span>
                            <span class="has-text-danger has-text-weight-bold">{{ row.losses }}</span>
                        </span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import LoadingSpinner2 from "../Common/LoadingSpinner2.vue";
    import { translate } from "../Common/translations";
    import { MethodBucketSlice, RecordProfileStatistics } from "../Api/recordProfile";

    const props = defineProps<{
        statistics: RecordProfileStatistics | null;
        loading: boolean;
    }>();

    const BUCKET_COLORS: Record<string, string> = {
        submission: "#df691a",
        points: "#3e8ed0",
        decision: "#9b6bdf",
        knockout: "#d9534f",
        pin: "#5cb85c",
        ippon: "#e0b13a",
        injury: "#c96b98",
        other: "#6b7a89",
    };

    const isEmpty = computed(() => !props.statistics || props.statistics.winsTotal + props.statistics.lossesTotal === 0);

    const donuts = computed(() => {
        if (!props.statistics) {
            return [];
        }
        return [
            {
                title: translate("record_profile.result.win"),
                total: props.statistics.winsTotal,
                slices: props.statistics.winSlices,
            },
            {
                title: translate("record_profile.result.loss"),
                total: props.statistics.lossesTotal,
                slices: props.statistics.lossSlices,
            },
        ];
    });

    function colorForBucket(key: string): string {
        return BUCKET_COLORS[key] ?? BUCKET_COLORS.other;
    }

    function bucketLabel(key: string): string {
        return translate(`record_profile.statistics.bucket.${key}`);
    }

    function gradientFor(slices: MethodBucketSlice[]): string {
        let acc = 0;
        const segments = slices.map((slice, index) => {
            const from = acc;
            acc += slice.percentage;
            // Rounded percentages need not sum to exactly 100 - pin the last stop to 100%
            // so the conic gradient never shows a seam or a sliver of leftover track.
            const to = index === slices.length - 1 ? 100 : acc;
            return `${colorForBucket(slice.key)} ${from}% ${to}%`;
        });
        return `conic-gradient(${segments.join(", ")})`;
    }
</script>

<style scoped>
    .donuts {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;
        gap: 2rem;
    }

    .donut-block {
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 0;
        flex: 1 1 220px;
        max-width: 280px;
    }

    .donut-title {
        text-transform: uppercase;
        letter-spacing: 0.05em;
        margin-bottom: 0.75rem;
    }

    .donut {
        width: 168px;
        height: 168px;
        border-radius: 50%;
        position: relative;
        mask: radial-gradient(circle, transparent 54%, #000 55%);
        -webkit-mask: radial-gradient(circle, transparent 54%, #000 55%);
    }

    .donut-empty {
        background: #2a3947;
    }

    .donut-hole {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        mask: radial-gradient(circle, #000 53%, transparent 54%);
        -webkit-mask: radial-gradient(circle, #000 53%, transparent 54%);
    }

    .donut-caption {
        text-transform: lowercase;
    }

    .legend {
        margin-top: 1rem;
        width: 100%;
    }

    .legend-row {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.15rem 0;
        font-size: 0.85rem;
    }

    .legend-swatch {
        width: 0.85rem;
        height: 0.85rem;
        border-radius: 3px;
        flex-shrink: 0;
    }

    .legend-label {
        flex: 1;
    }

    .table td,
    .table th {
        padding: 0.25em 0.35em;
    }

    .table {
        font-size: 0.9em;
    }

    .method-cards {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .method-card {
        padding: 0.75rem 1rem;
    }

    .method-counts {
        gap: 1.5rem;
    }

    @media screen and (max-width: 768px) {
        .donuts {
            gap: 1.5rem;
        }

        .donut {
            width: 148px;
            height: 148px;
        }
    }
</style>
